/**
 * Shared types for Company Documents feature
 *
 * Documents are company-level files organized in folders with optional
 * job role targeting, signature requirements, and expiration tracking.
 */
import { z } from "zod";
import { EmployeeDocumentFolderTypeSchema } from "./employeeDocument.js";
import { DeliveryRuleInputSchema, DeliveryRuleResponseSchema } from "./documentDeliveryRule.js";
// ===== INPUT SCHEMAS =====
export const CreateDocumentInputSchema = z.object({
    fileUploadId: z.uuid(),
    name: z.string().min(1).max(255).optional(),
    teamMemberFolderType: EmployeeDocumentFolderTypeSchema.optional(),
    folderId: z.uuid().optional(), // null = root level
    signatureRequired: z.boolean().optional().default(false),
    expirationDate: z.iso.datetime().optional(),
    notes: z.string().optional(),
    jobRoleIds: z.array(z.uuid()).optional().default([]), // Job roles this document applies to
    deliveryRule: DeliveryRuleInputSchema.optional(),
    tagIds: z.array(z.uuid()).optional(), // Category tags for this document (e.g., I-9, W-4)
});
export const UpdateDocumentInputSchema = z.object({
    fileUploadId: z.uuid().optional(), // for re-upload
    name: z.string().min(1).max(255).nullable().optional(), // null = clear, undefined = no change
    teamMemberFolderType: EmployeeDocumentFolderTypeSchema.nullable().optional(), // null = clear
    folderId: z.uuid().nullable().optional(), // Can move to root with null
    signatureRequired: z.boolean().optional(),
    expirationDate: z.iso.datetime().nullable().optional(),
    notes: z.string().nullable().optional(),
    jobRoleIds: z.array(z.uuid()).optional(), // Replace job roles if provided
    deliveryRule: DeliveryRuleInputSchema.nullable().optional(), // null = remove rule, undefined = no change
    tagIds: z.array(z.uuid()).optional(), // Replace category tags if provided (undefined = no change)
});
export const ListDocumentsInputSchema = z.object({
    folderId: z.uuid().nullable().optional(), // Filter by folder (null = root level documents)
    jobRoleId: z.uuid().optional(), // Filter by job role
    signatureRequired: z.boolean().optional(), // Filter by signature requirement
    includeExpired: z.boolean().optional().default(true), // Include expired documents
});
// ===== RESPONSE SCHEMAS =====
// Simplified file info for document responses
export const DocumentFileInfoSchema = z.object({
    id: z.uuid(),
    originalFilename: z.string(),
    mimeType: z.string(),
    fileSize: z.number(),
    url: z.string().optional(), // Presigned URL if available
});
// Job role info for document responses
export const DocumentJobRoleInfoSchema = z.object({
    id: z.uuid(),
    name: z.string(),
});
export const DocumentResponseSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    name: z.string().nullable(),
    teamMemberFolderType: EmployeeDocumentFolderTypeSchema.nullable(),
    folderId: z.uuid().nullable(),
    signatureRequired: z.boolean(),
    expirationDate: z.iso.datetime().nullable(),
    notes: z.string().nullable(),
    createdAt: z.iso.datetime(),
    createdBy: z.uuid(),
    updatedAt: z.iso.datetime(),
    file: DocumentFileInfoSchema,
    jobRoles: z.array(DocumentJobRoleInfoSchema),
    deliveryRule: DeliveryRuleResponseSchema.nullable(),
    outOfComplianceCount: z.number().int().nonnegative().default(0),
    tagIds: z.array(z.uuid()).optional(), // Category tag IDs applied to this document
});
